var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import VoucherTemplate from '../pdf/VoucherTemplate';
import ReceiptTemplate from '../pdf/ReceiptTemplate';
const sanitizeFilePart = (value, fallback) => {
    const normalized = String(value !== null && value !== void 0 ? value : '')
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return normalized || fallback;
};
const buildVoucherFileName = (voucherData, currentPath) => {
    const entityName = voucherData.vendor_name || voucherData.contractor_name || voucherData.customer_name || 'record';
    const entityPart = sanitizeFilePart(entityName, 'record');
    let categoryPart = 'payment';
    let documentPart = 'voucher';
    if (currentPath === null || currentPath === void 0 ? void 0 : currentPath.includes('vendorpayment')) {
        categoryPart = 'vendor';
        documentPart = 'voucher';
    }
    else if (currentPath === null || currentPath === void 0 ? void 0 : currentPath.includes('contractorpayment')) {
        categoryPart = 'contractor';
        documentPart = 'voucher';
    }
    else if (currentPath === null || currentPath === void 0 ? void 0 : currentPath.includes('salereceipt')) {
        categoryPart = 'sale';
        documentPart = 'receipt';
    }
    else if (currentPath === null || currentPath === void 0 ? void 0 : currentPath.includes('constructionreceipt')) {
        categoryPart = 'construction';
        documentPart = 'receipt';
    }
    const documentNumber = voucherData.voucher_number
        || voucherData.voucher_no
        || voucherData.receipt_number
        || 'document';
    const numberPart = sanitizeFilePart(documentNumber, 'document');
    return `${entityPart}_${categoryPart}_${documentPart}_${numberPart}.pdf`;
};
const resolveTemplateType = (currentPath) => {
    if ((currentPath === null || currentPath === void 0 ? void 0 : currentPath.includes('salereceipt')) || (currentPath === null || currentPath === void 0 ? void 0 : currentPath.includes('constructionreceipt'))) {
        return 'receipt';
    }
    return 'voucher';
};
const numberToWords = (num) => {
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    if (num === 0)
        return 'Zero';
    const convertLessThanThousand = (n) => {
        if (n === 0)
            return '';
        if (n < 10)
            return units[n];
        if (n < 20)
            return teens[n - 10];
        if (n < 100)
            return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + units[n % 10] : '');
        return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    const convert = (n) => {
        if (n < 1000)
            return convertLessThanThousand(n);
        if (n < 100000)
            return convertLessThanThousand(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convertLessThanThousand(n % 1000) : '');
        if (n < 10000000)
            return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 !== 0 ? ' ' + convert(n % 100000) : '');
        return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 !== 0 ? ' ' + convert(n % 10000000) : '');
    };
    return convert(num);
};
export const generatePDFFromElement = (elementId, filename) => __awaiter(void 0, void 0, void 0, function* () {
    const element = document.getElementById(elementId);
    if (!element) {
        console.error('Element not found for PDF generation');
        return;
    }
    try {
        const canvas = yield html2canvas(element, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff'
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.72);
        const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
        const imgX = (pdfWidth - imgWidth * ratio) / 2;
        const imgY = 30;
        pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, imgHeight * ratio, undefined, 'FAST');
        pdf.save(filename);
    }
    catch (error) {
        console.error('Error generating PDF:', error);
    }
});
export const generateVoucherPDF = (voucherData, currentPath, companyInfo) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    if (!voucherData) {
        console.error('No voucher data provided for PDF generation');
        return;
    }
    const items = Array.isArray(voucherData.items) ? voucherData.items : [];
    const amountValue = Number((_d = (_c = (_b = (_a = voucherData.amount_paid) !== null && _a !== void 0 ? _a : voucherData.total_amount) !== null && _b !== void 0 ? _b : voucherData.total) !== null && _c !== void 0 ? _c : voucherData.amount) !== null && _d !== void 0 ? _d : items.reduce((sum, item) => sum + Number(item.amount || 0), 0));
    const formattedData = Object.assign(Object.assign({}, voucherData), { total: amountValue, company_name: (companyInfo === null || companyInfo === void 0 ? void 0 : companyInfo.name) || voucherData.company_name || 'VEERA RAGHAVAR CONSTRUCTIONS', address: (companyInfo === null || companyInfo === void 0 ? void 0 : companyInfo.address) || voucherData.address || 'No.43, Walajabad, Manivakkam Main Rd, Tamil Nadu 600048', phone: voucherData.phone || '+91 12345 67890', amount_in_words: voucherData.amount_in_words || numberToWords(Math.round(amountValue)) + ' Rupees Only', items });
    const templateType = resolveTemplateType(currentPath);
    const TemplateComponent = templateType === 'receipt' ? ReceiptTemplate : VoucherTemplate;
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    container.style.width = '794px';
    container.style.height = '1123px';
    container.style.overflow = 'hidden';
    container.style.opacity = '1';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '-1000';
    container.style.background = '#ffffff';
    document.body.appendChild(container);
    const root = createRoot(container);
    root.render(createElement(TemplateComponent, { data: formattedData }));
    const waitForRender = () => __awaiter(void 0, void 0, void 0, function* () {
        yield Promise.resolve();
        yield new Promise((resolve) => setTimeout(resolve, 0));
        yield new Promise((resolve) => requestAnimationFrame(() => resolve()));
        yield Promise.race([
            new Promise((resolve) => {
                const checkImages = () => {
                    const images = Array.from(container.querySelectorAll('img'));
                    if (images.every((image) => image.complete)) {
                        resolve();
                        return;
                    }
                    setTimeout(checkImages, 50);
                };
                checkImages();
            }),
            new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
        yield new Promise((resolve) => requestAnimationFrame(() => resolve()));
    });
    try {
        yield waitForRender();
        const canvas = yield html2canvas(container, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            windowWidth: 794,
            windowHeight: 1123,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.72);
        const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        pdf.save(buildVoucherFileName(voucherData, currentPath));
    }
    catch (error) {
        console.error('Error generating voucher PDF:', error);
    }
    finally {
        root.unmount();
        container.remove();
    }
});
